import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { ReactionSummary } from '@avslack/shared';
import { EMOJI_GROUPS, searchEmoji } from '../data/emoji';
import { useCustomEmoji } from '../data/custom-emoji';
import { useFrequentEmoji } from '../data/emoji-usage';
import { CustomEmojiImage, ReactionGlyph } from './Emoji';
import { useUsers } from '../data/users';

/** Jel -> kimondhato nev (az elso kulcsszo): a kepernyoolvaso NEVET mondjon, ne nyers jelet. */
const EMOJI_NAMES = new Map(
  EMOJI_GROUPS.flatMap((group) =>
    group.items.map((item) => [item.char, item.keywords[0]] as const),
  ),
);

function emojiName(emoji: string): string {
  const custom = /^:(.+):$/.exec(emoji);
  if (custom) return custom[1]!;
  return EMOJI_NAMES.get(emoji) ?? emoji;
}

interface ReactionBarProps {
  reactions: ReactionSummary[];
  meId: string;
  onToggle: (emoji: string) => void;
}

export function ReactionBar({ reactions, meId, onToggle }: ReactionBarProps) {
  const { nameOf } = useUsers();
  if (reactions.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {reactions.map((reaction) => {
        const mine = reaction.userIds.includes(meId);
        const names = reaction.userIds
          .map((id) => (id === meId ? 'Te' : nameOf(id, 'Valaki')))
          .join(', ');

        return (
          <button
            key={reaction.emoji}
            type="button"
            title={`${names} reagált: ${reaction.emoji}`}
            aria-label={`${emojiName(reaction.emoji)}: ${reaction.count}`}
            aria-pressed={mine}
            onClick={() => onToggle(reaction.emoji)}
            className={[
              'flex items-center gap-1 rounded-pill border px-2 py-0.5 text-xs',
              'transition-colors duration-(--duration-quick)',
              mine
                ? 'border-accent bg-accent-soft text-accent'
                : 'border-border-soft bg-surface hover:border-border-strong',
            ].join(' ')}
          >
            <ReactionGlyph emoji={reaction.emoji} />
            <span className="font-semibold tabular-nums">{reaction.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Emoji-valaszto.
 *
 * Kereshető, csoportokba rendezett keszlet. Legelol a SAJAT leggyakoribb jelek
 * (a szerver szamolja a reakciokbol es az elkuldott uzenetekbol, lasd
 * data/emoji-usage.ts), utana a workspace sajat emojijai.
 * A kereses MAGYAR kulcsszavakra megy (lasd data/emoji.ts): magyarul beszelunk,
 * tehat magyarul is keresunk - egy angol nevekre epulo kereso itt hasznalhatatlan
 * lenne.
 *
 * Ket helyen kell ugyanez a keszlet: az asztali lebego savban egy popoverben,
 * mobilon pedig a hosszu nyomasra nyilo lapban. Ezert a valaszto TARTALMA
 * kulon all ({@link EmojiChooser}), es a keret valtozik korulotte - nem a
 * masolat.
 */
export function EmojiPicker({
  onPick,
  focusOnClose,
  children,
}: {
  onPick: (emoji: string) => void;
  /**
   * Hova menjen a fokusz bezaraskor. Alapbol a Radix a nyito gombra adja
   * vissza - az uzenetszerkesztonel ez rossz: a kovetkezo Enter nem uzenetet
   * kuld, hanem ujra kinyitja a valasztot.
   */
  focusOnClose?: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="end"
          onCloseAutoFocus={
            focusOnClose
              ? (event) => {
                  event.preventDefault();
                  focusOnClose();
                }
              : undefined
          }
          className="animate-fade-rise z-50 w-72 rounded-card border border-border-soft bg-surface-raised p-2 shadow-(--shadow-modal)"
        >
          <EmojiChooser
            autoFocus
            onPick={(emoji) => {
              setOpen(false);
              onPick(emoji);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * A valaszto tartalma: keresomezo + talalatok.
 *
 * Keret nelkul, hogy popoverbe es also lapba is beilleszthető legyen.
 */
export function EmojiChooser({
  onPick,
  autoFocus = false,
  height = 'max-h-64',
}: {
  onPick: (emoji: string) => void;
  /**
   * Mobilon NEM fokuszalunk: a felugro billentyuzet elfedne a talalatokat,
   * pedig a leggyakoribb mozdulat epp a keresés nelkuli valasztas.
   */
  autoFocus?: boolean;
  height?: string;
}) {
  const [query, setQuery] = useState('');
  const { emoji: custom } = useCustomEmoji();
  const frequent = useFrequentEmoji();

  const groups = searchEmoji(query, frequent.rank);
  const needle = query.trim().toLowerCase();
  // Kereseskor a talalatok maguk is a hasznalat szerint allnak, kulon sor nem kell.
  const frequentItems = needle ? [] : frequent.items;
  const customHits = needle
    ? custom.filter((item) => item.name.includes(needle))
    : custom.slice(0, 24);

  return (
    <div>
      <input
        type="search"
        autoComplete="off"
        spellCheck={false}
        aria-label="Emoji keresése"
        autoFocus={autoFocus}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Keresés… (pl. kávé, kutya, pipa)"
        className="mb-2 w-full rounded-control border border-border bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent"
      />

      <div className={`${height} overflow-y-auto overscroll-contain pr-0.5`}>
        {frequentItems.length > 0 && (
          <section className="mb-2">
            <h3 className="px-1 pb-1 text-2xs uppercase tracking-wide text-ink-faint">Gyakori</h3>
            <div className="grid grid-cols-7 gap-0.5">
              {frequentItems.map((item) =>
                item.kind === 'custom' ? (
                  <button
                    key={`:${item.emoji.name}:`}
                    type="button"
                    title={`:${item.emoji.name}:`}
                    onClick={() => onPick(`:${item.emoji.name}:`)}
                    className="grid size-8 place-items-center rounded-control text-lg transition-colors duration-(--duration-quick) hover:bg-surface-hover"
                  >
                    <CustomEmojiImage emoji={item.emoji} />
                  </button>
                ) : (
                  <button
                    key={item.entry.char}
                    type="button"
                    title={item.entry.keywords[0]}
                    onClick={() => onPick(item.entry.char)}
                    className="grid size-8 place-items-center rounded-control text-lg transition-colors duration-(--duration-quick) hover:bg-surface-hover"
                  >
                    {item.entry.char}
                  </button>
                ),
              )}
            </div>
          </section>
        )}

        {customHits.length > 0 && (
          <section className="mb-2">
            <h3 className="px-1 pb-1 text-2xs uppercase tracking-wide text-ink-faint">
              Saját emojik
            </h3>
            <div className="grid grid-cols-7 gap-0.5">
              {customHits.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  title={`:${item.name}:`}
                  onClick={() => onPick(`:${item.name}:`)}
                  className="grid size-8 place-items-center rounded-control text-lg transition-colors duration-(--duration-quick) hover:bg-surface-hover"
                >
                  <CustomEmojiImage emoji={item} />
                </button>
              ))}
            </div>
          </section>
        )}

        {groups.map((group) => (
          // A teljes keszlet tobb szaz gomb: a kepernyon kivuli szekciot a bongeszo NE rajzolja ki.
          // Az `auto` megjegyzi a mar kirajzolt meretet, igy a gorgetosav nem ugral.
          <section
            key={group.title}
            className="mb-2 [contain-intrinsic-size:auto_400px] [content-visibility:auto] last:mb-0"
          >
            <h3 className="px-1 pb-1 text-2xs uppercase tracking-wide text-ink-faint">
              {group.title}
            </h3>
            <div className="grid grid-cols-7 gap-0.5">
              {group.items.map((item) => (
                <button
                  key={item.char}
                  type="button"
                  title={item.keywords[0]}
                  onClick={() => onPick(item.char)}
                  className="grid size-8 place-items-center rounded-control text-lg transition-colors duration-(--duration-quick) hover:bg-surface-hover"
                >
                  {item.char}
                </button>
              ))}
            </div>
          </section>
        ))}

        {groups.length === 0 && customHits.length === 0 && (
          <p className="px-1 py-6 text-center text-xs text-ink-faint">
            Nincs találat erre: „{query.trim()}”
          </p>
        )}
      </div>
    </div>
  );
}
